import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Trophy, Heart, Globe, Users, Calendar, CheckCircle, Lock } from "lucide-react";

const DUO_ACHIEVEMENTS = [
  { id: "duo_first_shared_trip", icon: Globe, title: "First Shared Trip", desc: "Visit a country your partner also visited" },
  { id: "duo_5_shared", icon: Globe, title: "Travel Partners", desc: "Share 5 countries" },
  { id: "duo_10_shared", icon: Globe, title: "Inseparable", desc: "Share 10 countries" },
  { id: "duo_same_continent", icon: Globe, title: "Same Wavelength", desc: "Both visit the same continent" },
  { id: "duo_bucket_5", icon: CheckCircle, title: "Shared Dreams", desc: "Complete 5 shared items" },
  { id: "duo_bucket_15", icon: CheckCircle, title: "Dream Team", desc: "Complete 15 shared items" },
  { id: "duo_bucket_30", icon: CheckCircle, title: "Bucket Crushers", desc: "Complete 30 shared items" },
  { id: "duo_first_expense", icon: Users, title: "Splitting Bills", desc: "Track your first shared expense" },
  { id: "duo_anniversary", icon: Calendar, title: "Anniversary Trip", desc: "Stay linked for 1 year" },
];

const DuoAchievementsCard = () => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.partner_id) { setLoading(false); return; }
    axios.get(`${API}/achievements`)
      .then(res => {
        const earned = (res.data.achievements || []).filter(a => a.category === "duo" || a.badge_id?.startsWith("duo_"));
        setAchievements(earned.map(a => a.badge_id));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.partner_id]);

  if (!user?.partner_id || loading) return null;

  const earned = achievements.length;
  const total = DUO_ACHIEVEMENTS.length;

  return (
    <div className="bg-card border border-border rounded-2xl p-4" data-testid="duo-achievements-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          <h3 className="text-base font-semibold text-foreground">Duo Achievements</h3>
        </div>
        <span className="text-xs text-muted-foreground">{earned}/{total}</span>
      </div>

      <div className="space-y-2.5">
        {DUO_ACHIEVEMENTS.map(ach => {
          const Icon = ach.icon;
          const isEarned = achievements.includes(ach.id);
          return (
            <div key={ach.id} className={`flex items-center gap-3 p-2.5 rounded-xl border transition-colors ${isEarned ? "bg-amber-500/10 border-amber-500/20" : "bg-muted/30 border-border/50"}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isEarned ? "bg-amber-500/20" : "bg-muted"}`}>
                {isEarned ? <Icon className="w-4.5 h-4.5 text-amber-500" /> : <Lock className="w-4 h-4 text-muted-foreground/50" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isEarned ? "text-foreground" : "text-muted-foreground"}`}>{ach.title}</p>
                <p className="text-xs text-muted-foreground">{ach.desc}</p>
              </div>
              {isEarned && <Heart className="w-4 h-4 text-pink-500 flex-shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DuoAchievementsCard;
